import { SelectProps } from '@amzn/awsui-components-core';

/**
 * Determines the sort priority of a product line option.
 * - Names ending with "Device" variants sort first (priority 0)
 * - Names ending with "Accessories" variants sort last (priority 2)
 * - Everything else sorts in between (priority 1)
 *
 * @param label - The option label to evaluate
 * @returns A numeric priority: 0 (device/top), 1 (default/middle), 2 (accessories/bottom)
 */
const getOptionSortPriority = (label: string): number => {
  const upperLabel = label.toUpperCase().trim();

  if (upperLabel.endsWith('DEVICE') || upperLabel.endsWith('DEVICES') || upperLabel.endsWith('- DEVICE')) {
    return 0;
  }

  if (
    upperLabel.endsWith('ACCESSORY') ||
    upperLabel.endsWith('ACCESSORIES') ||
    upperLabel.endsWith('- ACCESSORY') ||
    upperLabel.endsWith('- ACCESSORIES')
  ) {
    return 2;
  }

  return 1;
};

/**
 * Extracts the RPN count from a labelTag string (e.g., "5 RPNs" -> 5, "0 RPNs" -> 0).
 * Returns null if the labelTag is not present or doesn't match the expected format.
 *
 * @param labelTag - The labelTag string from a SelectProps.Option
 * @returns The numeric RPN count, or null if not parseable
 */
const getRpnCountFromLabelTag = (labelTag: string | undefined): number | null => {
  if (!labelTag) return null;
  const match = labelTag.match(/^(\d+)\s/);
  return match ? parseInt(match[1], 10) : null;
};

/**
 * Generic comparator function for three-tier sorting:
 * 1. Primary sort: Device suffix first, Accessories last
 * 2. Secondary sort: Items with 0 count go to the bottom (when count is present)
 * 3. Tertiary sort: Alphabetical by name
 *
 * @param getName - Function to extract the name/label from an item
 * @param getCount - Function to extract the count from an item (return null if not applicable)
 * @returns A comparator function suitable for Array.sort()
 */
export const createThreeTierSortComparator = <T>(
  getName: (item: T) => string,
  getCount?: (item: T) => number | null
) => {
  return (a: T, b: T): number => {
    // Primary sort: Device suffix first, Accessories last
    const priorityA = getOptionSortPriority(getName(a));
    const priorityB = getOptionSortPriority(getName(b));

    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    // Secondary sort: Items with 0 count go to the bottom (when count is present)
    if (getCount) {
      const countA = getCount(a);
      const countB = getCount(b);

      if (countA !== null && countB !== null) {
        const isZeroA = countA === 0;
        const isZeroB = countB === 0;

        if (isZeroA !== isZeroB) {
          return isZeroA ? 1 : -1;
        }
      }
    }

    // Tertiary sort: Alphabetical by name
    return getName(a).localeCompare(getName(b));
  };
};

/**
 * Sorts product line options using three-tier sorting rules:
 * 1. Primary sort: Device suffix first, Accessories last
 * 2. Secondary sort: Options with 0 RPNs go to the bottom (when labelTag is present)
 * 3. Tertiary sort: Alphabetical by label
 *
 * @param options - Array of SelectProps.Option to sort
 * @returns Sorted array of SelectProps.Option
 *
 * @example
 * ```typescript
 * const sorted = sortProductLineOptions(options);
 * // Result: Device options, then other options, then Accessories
 * //          Within each group: non-zero RPN options first, then 0 RPNs
 * //          Within each subgroup: alphabetically sorted
 * ```
 */
export const sortProductLineOptions = (options: SelectProps.Option[]): SelectProps.Option[] => {
  const comparator = createThreeTierSortComparator<SelectProps.Option>(
    (option) => option.label || '',
    (option) => getRpnCountFromLabelTag(option.labelTag)
  );

  return [...options].sort(comparator);
};
